import React, { useEffect, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Tooltip } from "antd";
import { pathOr } from "ramda";
import { If, Then, Else } from "react-if";

import CustomInput from "../../../../components/customInput";
import CustomButton from "../../../../components/customButton";
import CommonService from "../../../../service/CommonService";
import CustomModal from "../../../../components/customModal";
import CommonInput from "../../../../components/commonFields";
import CustomLabel from "../../../../components/customLabel";
import { setPopup } from "../../../../actions/popupAction";
import EditTemplateModal from "./components/editTemplateModal";
import { debounceAsync } from "../../../../helper/helpers";
import { MultipleFormikSelect } from "../../../../components/select/multipleSelect";
import { RcsPreview } from "../BroadcastMessage/rcsContainers/rcsPreview";
import { DEBOUNCE_TIME } from "../../../../constants/constants";
import { ERRORS } from "../../constants";
import "./styles.scss";

const SendMessageRcs = (props) => {
  const commonService = new CommonService();

  const {
    headers = {},
    setFieldValue = () => {},
    formValues = {},
    data = [],
    touched,
    errors,
    type,
    name,
    tempName,
    listOfTemplates,
    saveChangesAllowed = false,
    fetchAccounts,
    accountsList = [],
    selectedRcsTemplate,
    isTemplate = false,
    parentState,
    selectedTempType,
    templates,
    allAccountsList = [],
    onResetEditTemplate = () => {},
    hasFieldVariables = true,
  } = props;
  const { enableOptOut, optOutMessage } = formValues;

  const [state, setState] = useState({
    addVariables: false,
    saveTemplateModal: false,
    editTemplate: false,
    editTemplateModal: false,
    templateName: "",
    error: "",
    characters: 0,
    words: 0,
    previewText: "",
    showAs: saveChangesAllowed ? "default" : "custom",
  });

  const dispatch = useDispatch();
  const saveRef = useRef(false);
  const ref = useRef();

  const currentUser = useSelector((state) => state.login.user);

  const isTemplateModificationAllowed = ["OU", "TS"].includes(
    currentUser?.roles?.roleKey,
  );

  const displayShowAs = saveChangesAllowed;

  useEffect(
    () => {
      const message = formValues[name] || "";
      const withoutBreaks = message.replace(/[\r\n]/gm, " ");
      setState((st) => ({
        ...st,
        characters: message.length,
        words:
          withoutBreaks.length > 0
            ? withoutBreaks
                .trim()
                .split(" ")
                .filter((elem) => elem.length > 0).length
            : 0,
      }));
    },
    [formValues, name],
  );

  useEffect(
    () => {
      setState((st) => ({ ...st, editTemplate: false, addVariables: false }));
      onResetEditTemplate();
    },
    [isTemplate],
  );

  const addText = (head) => {
    const header = `{${head}}`;
    const textArea = pathOr(
      null,
      ["current", "resizableTextArea", "textArea"],
      ref,
    );
    let text = (formValues[name] || "") + header;

    if (
      textArea &&
      (textArea.selectionStart || textArea.selectionStart === 0)
    ) {
      const startPos = textArea.selectionStart;
      const endPos = textArea.selectionEnd;
      text =
        textArea.value.substring(0, startPos) +
        header +
        textArea.value.substring(endPos, textArea.value.length);
    }
    setFieldValue(name, text);
  };

  const clearMessage = () => {
    setFieldValue(name, "");
    if (tempName) {
      setFieldValue(tempName, "");
    }
  };

  const toggleAddVariables = () => {
    setState((st) => ({
      ...st,
      addVariables: !st.addVariables,
    }));
  };

  const toggleSaveTemplate = () => {
    setState((st) => ({
      ...st,
      saveTemplateModal: !st.saveTemplateModal,
      error: "",
    }));
  };

  const handleOnChange = (e) => {
    const temp = e.target.value;
    setState((st) => ({
      ...st,
      templateName: temp,
      error: "",
    }));
  };

  const handleCancel = () => {
    setState((st) => ({
      ...st,
      saveTemplateModal: false,
      templateName: "",
      error: "",
    }));
  };

  const handleSaveTemplate = async () => {
    if (saveRef.current) return;
    try {
      saveRef.current = true;
      const accountIdsSelected = displayShowAs
        ? formValues.accounts.map((value) => value.practiceId || "all")
        : null;

      const response = await commonService.saveTemplateData(
        state.templateName,
        formValues[name],
        type,
        state.showAs,
        accountIdsSelected,
      );

      setState((st) => ({
        ...st,
        templateName: "",
        saveTemplateModal: false,
      }));

      setFieldValue("accounts", []);
      const successMessage = pathOr(
        "Template saved successfully.",
        ["data", "message"],
        response,
      );
      dispatch(
        setPopup({
          customPopupMessage: true,
          messageType: "success",
          message: successMessage,
        }),
      );
      if (listOfTemplates) {
        listOfTemplates();
      }
    } catch (err) {
      if (err?.response?.data?.message?.includes("already exists")) {
        setState((st) => ({ ...st, error: err.response.data.message }));
      } else {
        const errorMessage = pathOr(
          "Something went wrong!",
          ["response", "data", "message"],
          err,
        );
        dispatch(
          setPopup({
            customPopupMessage: true,
            messageType: "error",
            message: errorMessage,
          }),
        );
        setState((st) => ({
          ...st,
          templateName: "",
          saveTemplateModal: false,
        }));
      }
    } finally {
      saveRef.current = false;
    }
  };

  const handleUpdateTemplate = async (templateData) => {
    if (saveRef && saveRef.current) return;
    else {
      try {
        saveRef.current = true;

        const accountIdsSelected = displayShowAs
          ? templateData.accounts.map((value) => value.practiceId || "all")
          : null;

        if (templateData.saveAs === "existing") {
          const payload = {
            templateMessage: formValues[name],
            type,
            ...(accountIdsSelected ? { accounts: accountIdsSelected } : {}),
          };
          const templateId = formValues[selectedTempType].key;
          const response = await commonService.editTemplate(
            payload,
            templateId,
          );
          dispatch(
            setPopup({
              customPopupMessage: true,
              messageType: "success",
              message: pathOr(
                "Something went wrong!",
                ["data", "message"],
                response,
              ),
            }),
          );
          setFieldValue(tempName, formValues[name]);
          listOfTemplates();

          setState((st) => ({
            ...st,
            editTemplateModal: false,
            addVariables: false,
            editTemplate: false,
          }));

          parentState((st) => ({ ...st, templateInEditState: false }));
        } else {
          const response = await commonService.saveTemplateData(
            templateData.template ? templateData.template : state.templateName,
            formValues[name],
            type,
            templateData.showAs,
            accountIdsSelected,
          );
          if (response.data.statusCode === 200) {
            setState((st) => ({
              ...st,
              templateName: "",
              saveTemplateModal: "",
              editTemplateModal: false,
              addVariables: false,
              editTemplate: false,
            }));
            listOfTemplates();
            parentState((st) => ({ ...st, templateInEditState: false }));
            setFieldValue(name, formValues?.tempMessage);
            dispatch(
              setPopup({
                customPopupMessage: true,
                messageType: "success",
                message: pathOr(
                  "Sorry, could not update. Please refresh and try again!",
                  ["data", "message"],
                  response,
                ),
              }),
            );
          }
        }
        saveRef.current = false;
      } catch (err) {
        if (err.response?.data?.message !== ERRORS.TEMPLATE.NAME_EXISTS) {
          setState((st) => ({ ...st, error: err.response.data.message }));
        }
        saveRef.current = false;
      }
    }
  };

  const debouncedCheckTemplateExists = useCallback(
    debounceAsync(commonService.checkTemplateExists, DEBOUNCE_TIME, false),
    [],
  );

  useEffect(
    () => {
      if (
        !state.templateName.trim() ||
        (!formValues.accounts?.length && displayShowAs)
      ) {
        setState((st) => ({ ...st, error: "" }));
        return;
      }
      const accountIdsSelected = displayShowAs
        ? formValues.accounts?.map((value) => value.practiceId || "all")
        : [];

      debouncedCheckTemplateExists(state.templateName, type, accountIdsSelected)
        .then((response) => {
          if (response.data.exists) {
            setState((st) => ({ ...st, error: ERRORS.TEMPLATE.NAME_EXISTS }));
          } else {
            setState((st) => ({ ...st, error: "" }));
          }
        })
        .catch((err) => {
          console.error(err);
          setState((st) => ({ ...st, error: "Error checking template name" }));
        });
    },
    [
      state.templateName,
      formValues.accounts,
      type,
      debouncedCheckTemplateExists,
      displayShowAs,
    ],
  );

  // Template-related functions for RCS
  const handleCancelChanges = () => {
    setState((st) => ({
      ...st,
      editTemplate: false,
      addVariables: false,
    }));
    setFieldValue(name, formValues[tempName]);
    parentState((st) => ({ ...st, templateInEditState: false }));
  };

  const handleRemoveError = () => {
    setState((st) => ({
      ...st,
      error: "",
    }));
  };

  const templateKey = formValues?.template.key;

  const templateObj = templates?.find((item) => item.id === templateKey);

  const selectedTemplateAccounts = state.editTemplateModal
    ? allAccountsList.filter((val) =>
        templateObj?.accounts?.includes(val.practiceId),
      )
    : [];

  if (state.editTemplateModal && templateObj?.accounts?.[0] === "all") {
    selectedTemplateAccounts.push({ id: "all", name: "All" });
  }

  const isDefaultTemplate = templateObj?.showAs === "default" ? true : false;
  const canSaveEditedTemplate =
    (isTemplateModificationAllowed && !isDefaultTemplate) ||
    (!isTemplateModificationAllowed && isDefaultTemplate);
  const isSaveChangesDisabled =
    canSaveEditedTemplate ||
    (displayShowAs
      ? errors[name]
      : formValues[tempName]?.trim() === formValues[name]?.trim() ||
        errors[name]);

  const DesktopMessageActions = () => {
    return (
      <div className="message-container-buttons">
        <div className="audio-buttons-wrapper">
          <CustomButton
            btntype="clear"
            title="Save as Template"
            onMouseDown={toggleSaveTemplate}
            disabled={!/\S/.test(formValues[name])}
            className={!/\S/.test(formValues[name]) ? "disable-button" : ""}
          />
        </div>
        <div className="right-buttons">
          <section className="characters">
            {`${state.characters} char | ${state.words} words `}
          </section>
          {hasFieldVariables && (
            <div className="field-variables">
              <CustomButton
                btntype="clear"
                title={`${state.addVariables ? "-" : "+"} Add field variables`}
                onMouseDown={toggleAddVariables}
              />
              <div className="separator" />
            </div>
          )}
          <CustomButton
            btntype="clear"
            title="Reset"
            onMouseDown={clearMessage}
            disabled={!/\S/.test(formValues[name])}
            className={!/\S/.test(formValues[name]) ? "disable-button" : ""}
          />
        </div>
      </div>
    );
  };

  return (
    <section className="broadcast-form">
      {state.saveTemplateModal && (
        <CustomModal
          title="Save Message as Template"
          submitButtonText="Save Template"
          cancelButtonText="Cancel"
          handleDelete={handleSaveTemplate}
          handleCancel={handleCancel}
          isVisible={state.saveTemplateModal}
          disabled={
            !/\S/.test(state.templateName) ||
            !/^[A-Za-z0-9()@ _-]{1,255}$/.test(state.templateName) ||
            (displayShowAs && !formValues?.accounts?.length) ||
            !!state.error
          }
        >
          <div className="fields-wrapper">
            <section className="form-row save-template">
              <CustomLabel title="Template Name" required />
              <CommonInput
                placeholder="Enter Template Name"
                className={`custom-form-input ${
                  state.error ? "error-box" : ""
                }`}
                onChange={handleOnChange}
                value={state.templateName}
              />
              <div className="error-label mob-margin save-template-error">
                {state.error}
              </div>
            </section>
            {displayShowAs && (
              <section className="form-row">
                <MultipleFormikSelect
                  data={accountsList}
                  name="accounts"
                  optionLabelProp="name"
                  optionValueProp="id"
                  placeholder="Select account name"
                  required
                  title="Add to Accounts"
                  infiniteScroll
                  showAll={true}
                  loadMoreData={fetchAccounts}
                />
              </section>
            )}
          </div>
        </CustomModal>
      )}

      {state.editTemplateModal && (
        <EditTemplateModal
          title="Save Message as Template"
          submitButtonText="Save Template"
          cancelButtonText="Cancel"
          handleDelete={handleUpdateTemplate}
          handleRemoveError={handleRemoveError}
          handleCancel={() =>
            setState((st) => ({ ...st, editTemplateModal: false }))
          }
          isVisible={state.editTemplateModal}
          templateName={templateObj?.templateName}
          responseError={state.error}
          user={templateObj?.user}
          showAs={isDefaultTemplate ? "default" : "custom"}
          accountsList={accountsList}
          fetchAccounts={fetchAccounts}
          selectedAccounts={selectedTemplateAccounts}
          displayShowAs={displayShowAs}
          formValues={formValues}
          tempName={tempName}
          name={name}
          type={type}
        />
      )}

      <div className="messages-container">
        <section className="send-message-with-variables-wrapper">
          <div
            className={`send-message-container ${
              isTemplate && !state.editTemplate ? "disabled" : ""
            } ${touched[name] && errors[name] ? "error-box" : ""}`}
          >
            <If condition={isTemplate}>
              <Then>
                <If condition={state.editTemplate}>
                  <Then>
                    <CustomInput
                      inputRef={ref}
                      type="textarea"
                      name={name}
                      resize="none"
                      placeholder="Type message here..."
                      className="send-message-textarea"
                    />
                  </Then>
                  <Else>
                    <div className="content">{formValues[name]}</div>
                  </Else>
                </If>

                <div className="message-container-buttons">
                  <If condition={state.editTemplate}>
                    <Then>
                      <div className="left-buttons">
                        <CustomButton
                          btntype="clear"
                          title="Cancel"
                          onClick={() => handleCancelChanges()}
                          className="cancel-button"
                        />

                        <div className="separator" />
                        <CustomButton
                          btntype="clear"
                          title="Save Changes"
                          onClick={() => {
                            if (isSaveChangesDisabled) return;
                            setState((st) => ({
                              ...st,
                              editTemplateModal: true,
                              error: "",
                            }));
                          }}
                          disabled={isSaveChangesDisabled}
                          className={
                            isSaveChangesDisabled ? "disable-button" : ""
                          }
                        />
                      </div>

                      <div className="right-buttons">
                        {hasFieldVariables && (
                          <>
                            <section className="characters">
                              {`${state.characters} char | ${
                                state.words
                              } words `}
                            </section>
                            <CustomButton
                              btntype="clear"
                              title={`${
                                state.addVariables ? "-" : "+"
                              } Add field variables`}
                              onClick={() =>
                                setState((st) => ({
                                  ...st,
                                  addVariables: !state.addVariables,
                                }))
                              }
                            />
                            <div className="separator" />
                          </>
                        )}
                        <CustomButton
                          btntype="clear"
                          title="Reset"
                          onClick={() => {
                            setFieldValue(name, "");
                          }}
                          disabled={!/\S/.test(formValues[name])}
                          className={
                            !/\S/.test(formValues[name]) ? "disable-button" : ""
                          }
                        />
                      </div>
                    </Then>
                    <Else>
                      {formValues[selectedTempType] && (
                        <>
                          <CustomButton
                            btntype="clear"
                            title="Edit Template Message"
                            onClick={() => {
                              setState((st) => ({
                                ...st,
                                editTemplate: true,
                              }));
                              parentState((st) => ({
                                ...st,
                                templateInEditState: true,
                              }));
                            }}
                            className="edit-template-button"
                          />
                          <section className="characters">
                            {`${state.characters} char | ${state.words} words `}
                          </section>
                        </>
                      )}
                    </Else>
                  </If>
                </div>
              </Then>

              <Else>
                <>
                  <CustomInput
                    inputRef={ref}
                    type="textarea"
                    name={name}
                    resize="none"
                    placeholder="Type message here..."
                    className="send-message-textarea"
                    errorStyle="static"
                  />

                  <DesktopMessageActions />
                </>
              </Else>
            </If>
          </div>

          <If condition={state.addVariables}>
            <Then>
              <section className="custom-modal-search">
                <section className="custom-modal-tags-wrapper">
                  <div className="tags-container">
                    {headers.map((header, index) => (
                      <Tooltip
                        destroyTooltipOnHide
                        title={header.Header}
                        key={`tag-no-${index}`}
                      >
                        <p
                          className="tags"
                          onClick={() => addText(header.Header)}
                        >
                          {header.Header}
                        </p>
                      </Tooltip>
                    ))}
                  </div>
                </section>
              </section>
            </Then>
          </If>
        </section>
        <div className="rcs-preview-container">
          <div className="rcs-preview__content">
            <RcsPreview
              data={selectedRcsTemplate}
              values={formValues[name]}
              sampleData={data}
              headers={headers}
              optOutMessage={optOutMessage}
              enableOptOut={enableOptOut}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default SendMessageRcs;
